// =====================================================
// Política de contraseñas compartida (reset y cambio obligatorio)
//
// La validación real debe estar también en Supabase Auth
// (Authentication → Policies: longitud mínima, requisitos y
// "Leaked password protection"); esto es la primera barrera en cliente.
// =====================================================

export const MIN_PASSWORD_LENGTH = 12;

// Devuelve un mensaje de error o '' si la contraseña cumple la política.
export function validatePassword(password, { email } = {}) {
  if (!password || password.length < MIN_PASSWORD_LENGTH) {
    return `La contraseña debe tener al menos ${MIN_PASSWORD_LENGTH} caracteres.`;
  }
  if (password.length > 128) return 'La contraseña no puede superar los 128 caracteres.';
  const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((re) => re.test(password)).length;
  if (classes < 3) {
    return 'Combina al menos tres de estos tipos: minúsculas, mayúsculas, números y símbolos.';
  }
  if (/(.)\1{3,}/.test(password)) return 'La contraseña no puede repetir el mismo carácter tantas veces seguidas.';
  const lower = password.toLowerCase();
  const local = (email || '').split('@')[0].toLowerCase();
  if (local.length >= 3 && lower.includes(local)) return 'La contraseña no puede contener tu nombre de usuario.';
  if (/(impact ?hub|lizzi|password|contrase(ñ|n)a|qwerty|123456)/i.test(password)) {
    return 'La contraseña contiene palabras demasiado previsibles.';
  }
  return '';
}

// Comprueba la contraseña contra Have I Been Pwned con k-anonimato:
// solo salen del navegador los 5 primeros caracteres del hash SHA-1.
// Si el servicio no responde, no bloquea (devuelve false).
export async function isPasswordPwned(password) {
  try {
    const buf = await crypto.subtle.digest('SHA-1', new TextEncoder().encode(password));
    const hash = Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('').toUpperCase();
    const prefix = hash.slice(0, 5);
    const suffix = hash.slice(5);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 4000);
    const res = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`, {
      headers: { 'Add-Padding': 'true' },
      signal: controller.signal,
    });
    clearTimeout(timer);
    if (!res.ok) return false;
    const body = await res.text();
    return body.split('\n').some((line) => {
      const [suf, count] = line.trim().split(':');
      return suf === suffix && Number(count) > 0;
    });
  } catch (e) {
    return false;
  }
}

// Lee los claims del access token (sin verificar firma: solo para decidir UI;
// la autorización real la hace Supabase en el servidor).
export function readJwtClaims(accessToken) {
  try {
    const payload = accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), '='))
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
    );
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
}

// Una sesión de recuperación válida: el JWT trae amr "recovery" (o "otp")
// emitido hace menos de `maxAgeSec`.
export function isRecentRecoverySession(session, maxAgeSec = 3600) {
  const claims = session?.access_token ? readJwtClaims(session.access_token) : null;
  const amr = Array.isArray(claims?.amr) ? claims.amr : [];
  const entry = amr.find((a) => a?.method === 'recovery' || a?.method === 'otp');
  if (!entry) return false;
  const ts = Number(entry.timestamp) || 0;
  return Date.now() / 1000 - ts < maxAgeSec;
}
